import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react"
import { connect } from "react-redux"
import { Spinner } from "reactstrap"
import SweetAlert from "react-bootstrap-sweetalert"
import { useToasts } from "react-toast-notifications"
import BandEditor from "components/Common/SponsorBand/BandEditor"
import ListenerComponent from "components/Common/Socket/ListenerComponent"
import { store as socketStore } from "components/Common/Socket/store"
import * as actions from "../../../actions"
import { updateEventSponsors } from "../../../api"
import { invalidateEventCache } from "../../../eventCachePolicy"
import { createEventOperationId } from "../../../eventOperationId"
import SponsorForm from "./SponsorForm"
import SponsorsTable from "./SponsorsTable"
import { eventBand } from "./sponsorBand"
import { eventSponsorRows, sponsorFromForm, sponsorsWritePayload } from "./sponsorPayload"

// Sponsors — the sponsors of THIS event, the "Event Sponsors" section of its public page. Sportya's
// own partners are the other model: they sit above the footer of every page and are administered
// under Parteneri (ADR-0016: two models, one rendering component, two places to administer).
//
// The band at the top is the editor, as on Parteneri: a picture of the public section, and
// arranging it is arranging the real thing. Every gesture — add, edit, move, re-tier, remove — is
// one write of the whole list. The server mints the ids, so a row added here leaves without one
// and the stored list, ids included, comes back on the socket; that reply is what the screen
// settles on. `GET /events/:id` is not cached by the gateway, so the public page shows the change
// on its next load — there is no "within 15 minutes" to promise here.

export const SPONSORS_SAVED = "SUCCESSFULLY_UPDATED_EVENT_SPONSORS"

// The reply normally lands within a second. If it does not — the socket is down, or the reply went
// to another tab — the stored list is re-read rather than trusting the optimistic one forever.
export const RECONCILE_FALLBACK_MS = 4000

const REARRANGED = "Sponsors rearranged."

// Why a save was refused, in words: the API's codes are never shown. Events refuses on the socket; the gateway refuses the request (4xx).
const NO_ACCESS = "Your account cannot change this event's sponsors."
const REFUSALS = {
  SOMETHING_WENT_WRONG: "The sponsors were not saved: the server could not store the list. Showing the sponsors as stored; try again.",
  EVENT_NOT_FOUND: "This event no longer exists, so its sponsors were not saved.",
  ERROR_PERMISSIONS: NO_ACCESS,
  FORBIDDEN: NO_ACCESS,
  SESSION_EXPIRED: "Your session has expired. Sign in again, then repeat the change.",
}
// Any other request the gateway refused: its validation names a sponsor's field.
const FIELDS_REFUSED = "The sponsors were not saved: a sponsor's name, logo, tier or website was refused. Showing the sponsors as stored."
// No answer, a 5xx or a code nobody maps: the outcome is not known, and the list is read again.
const NOT_SAVED = "The sponsors could not be saved — reloading."
const refusal = code => typeof code === "string" && Object.prototype.hasOwnProperty.call(REFUSALS, code) ? REFUSALS[code] : null
const sendFailure = error => {
  const status = error?.response?.status
  // A 401 is always the session's: the gateway's sign-in gates answer it. Every other 4xx, a 429 or a 408 included, reads FIELDS_REFUSED.
  if (status === 401) return REFUSALS.SESSION_EXPIRED
  return status >= 400 && status < 500 ? refusal(error.response.data?.message) || FIELDS_REFUSED : NOT_SAVED
}

export const SponsorsTab = ({ id, activeEvent, getEventDetails }) => {
  const { addToast } = useToasts()
  const { state: { socket: { socketClientId } = {} } = {} } = useContext(socketStore)

  // null until the event has loaded; then the list as the editor shows it, applied optimistically
  // on every gesture and replaced by whatever the server says it stored.
  const [sponsors, setSponsors] = useState(null)
  // null when closed; { sponsor, defaultTier } while adding or editing.
  const [editing, setEditing] = useState(null)
  const [removing, setRemoving] = useState(null)
  const [saving, setSaving] = useState(false)

  // What the reply should say, and the fallback that fires if no reply comes. Refs, not state: the
  // socket listener is registered once and must read the current save, not the one it closed over.
  const pending = useRef(null)
  // The correlation of the latest save, kept after the fallback re-read the list: its late reply still carries the list as stored.
  const latest = useRef(null)

  useEffect(() => {
    if (!activeEvent?._id) return
    setSponsors(eventSponsorRows(activeEvent.sponsors))
  }, [activeEvent?._id, activeEvent?.sponsors]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => () => clearTimeout(pending.current?.fallback), [])

  const rows = useMemo(() => sponsors || [], [sponsors])
  const tiers = useMemo(() => eventBand.bandTiers(rows), [rows])
  const capacity = useMemo(() => eventBand.bandCapacity(rows), [rows])
  // The band's own order, flattened: the table reads top to bottom exactly as the band reads left
  // to right, so an arrow in one and a drag in the other are visibly the same move.
  const live = useMemo(() => tiers.flatMap(({ rows: tier }) => tier), [tiers])

  const settle = () => {
    clearTimeout(pending.current?.fallback)
    pending.current = null
    setSaving(false)
  }

  // The stored list is the truth again — after a failure, or when no reply came. Read from the server: a refused request
  // invalidates nothing, and the event read when the tab opened would otherwise come back from the read cache unchanged.
  const reload = useCallback(() => {
    settle()
    invalidateEventCache({ eventId: id })
    getEventDetails(id)
  }, [getEventDetails, id])

  // No reply in time: say so, and show what is actually stored rather than the optimistic list.
  const reloadUnconfirmed = useCallback(() => {
    addToast("No confirmation arrived — showing the sponsors as stored.", {
      appearance: "warning",
      autoDismiss: true,
    })
    reload()
  }, [addToast, reload])

  // Only the answer to the latest save counts: Events names the event and the save's correlation in every reply, so another
  // command's failure on the same topic, another tab's save or an older save's answer is left alone.
  const listener = useRef()
  listener.current = ({ success, message, data, eventId, correlationId } = {}) => {
    if (!correlationId || correlationId !== latest.current || String(eventId) !== String(id)) return
    // null once the fallback re-read the list: the late answer is taken quietly, and a late refusal changes nothing.
    const own = pending.current
    if (success === true && message === SPONSORS_SAVED) {
      settle()
      setSponsors(eventSponsorRows(data?.sponsors))
      if (own?.text) addToast(own.text, { appearance: "success", autoDismiss: true })
      return
    }
    if (success === false && own) {
      addToast(refusal(message) || NOT_SAVED, {
        appearance: "error",
        autoDismiss: true,
      })
      reload()
    }
  }
  const listenForResponse = useCallback(payload => listener.current(payload), [])

  /**
   * Writes the whole list back. The new list is applied locally first: an admin should see the plate
   * land where they dropped it, not a second later. The gateway answers 202; the reply on the socket
   * carries the list as stored — ids minted for new rows — and replaces the optimistic one.
   */
  const persist = async (next, text) => {
    if (saving) return
    setSponsors(next)
    setSaving(true)
    clearTimeout(pending.current?.fallback)
    // Letters, digits and hyphens, 16 to 128 of them: the edge proxy passes such a correlation through instead of replacing it.
    const correlationId = createEventOperationId("event-sponsors")
    latest.current = correlationId
    pending.current = { text, fallback: setTimeout(reloadUnconfirmed, RECONCILE_FALLBACK_MS) }

    try {
      await updateEventSponsors({
        eventId: id,
        sponsors: sponsorsWritePayload(next),
        socketClientId,
        correlationId,
      })
    } catch (error) {
      // A request that failed after another save went out describes a list the admin has moved past: that save settles on its own answer.
      if (latest.current !== correlationId) return
      addToast(sendFailure(error), {
        appearance: "error",
        autoDismiss: true,
      })
      reload()
    }
  }

  const move = ({ id: sponsorId, toTier, beforeId }) => {
    const { rows: next, writes } = eventBand.moveRow(rows, { id: sponsorId, toTier, beforeId })
    if (writes.length) persist(next, REARRANGED)
  }

  const shift = (sponsor, direction) => {
    const { rows: next, writes } = eventBand.shiftRow(rows, sponsor.id, direction)
    if (writes.length) persist(next, REARRANGED)
  }

  const saveSponsor = form => {
    const row = sponsorFromForm(
      form,
      eventBand.orderForForm(rows, { id: form.id, tier: form.category })
    )
    const next = form.id
      ? rows.map(sponsor => (sponsor.id === row.id ? row : sponsor))
      : [...rows, row]
    setEditing(null)
    persist(next, `${row.name} saved. The event page shows it on its next load.`)
  }

  const remove = sponsor => {
    setRemoving(null)
    persist(
      rows.filter(row => row.id !== sponsor.id),
      `${sponsor.name} was removed from the event.`
    )
  }

  return (
    <div className="mt-2">
      <ListenerComponent listenFunction={listenForResponse} topic="events-response" />

      <h5 className="mb-1">Event sponsors</h5>
      <p className="text-muted" style={{ fontSize: 13 }}>
        Only for this event. Sportya&apos;s own partners appear on every page and are managed under
        Parteneri.
      </p>

      {sponsors === null ? (
        <Spinner size="sm" animation="grow" variant="dark" />
      ) : (
        <>
          <BandEditor
            tiers={tiers}
            capacity={capacity}
            disabled={saving}
            title="The sponsors section, as it will render"
            description="Drag a plate to reorder it, drop it in another tier to re-tier it. Click a plate to edit the sponsor."
            editLabel="edit sponsor"
            addLabel={tierLabel => `Add a sponsor to ${tierLabel}`}
            onMove={move}
            onEdit={sponsor => setEditing({ sponsor })}
            onAdd={tier => setEditing({ sponsor: null, defaultTier: tier })}
          />

          <SponsorsTable
            sponsors={live}
            disabled={saving}
            onEdit={sponsor => setEditing({ sponsor })}
            onRetier={(sponsor, tier) => move({ id: sponsor.id, toTier: tier, beforeId: null })}
            onShift={shift}
            onRemove={setRemoving}
          />
        </>
      )}

      {editing && (
        <SponsorForm
          // Remounts on a different sponsor: the form owns its fields, and FileUpload owns its
          // preview with no way to reset it from outside.
          key={editing.sponsor?.id || `new-${editing.defaultTier}`}
          sponsor={editing.sponsor}
          defaultTier={editing.defaultTier}
          saving={saving}
          onSave={saveSponsor}
          onCancel={() => setEditing(null)}
        />
      )}

      {removing && (
        <SweetAlert
          warning
          title="Remove sponsor"
          showCancel
          confirmBtnText="Remove"
          confirmBtnBsStyle="danger"
          onConfirm={() => remove(removing)}
          onCancel={() => setRemoving(null)}
        >
          {removing.name} comes off the event page, and its logo is deleted from storage.
        </SweetAlert>
      )}
    </div>
  )
}

export default connect(null, { getEventDetails: actions.getEventDetails })(SponsorsTab)
